import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Spotlight, type SpotlightStore } from "@mantine/spotlight";

import { IconSearch } from "@tabler/icons-react";

import { useOmniSearchEntries } from "../hooks/useOmniSearchEntries";
import type { OmniSearchEntry, OmniSearchGroup } from "../types/omni-search";

type OmniSearchSpotlightProps = {
  /** The store the header trigger opens, so both talk to the same Spotlight. */
  store: SpotlightStore;
};

type EntryGroup = { group: OmniSearchGroup; entries: OmniSearchEntry[] };

/**
 * The search modal. `mod + K` opens it, typing filters the pages the user can open, and choosing a
 * result (click or Enter) closes the modal and goes to that page. Spotlight clears the query once
 * the modal has closed.
 */
export function OmniSearchSpotlight({ store }: OmniSearchSpotlightProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const { entries, loadLabsIntegration } = useOmniSearchEntries(query);
  const entryGroups = groupEntries(entries);

  return (
    <Spotlight.Root
      scrollable
      store={store}
      query={query}
      onQueryChange={setQuery}
      data-testid="omni-search-spotlight"
      onSpotlightOpen={loadLabsIntegration}
    >
      <Spotlight.Search
        data-testid="omni-search-input"
        aria-label="Search modules and settings"
        placeholder="Search modules and settings…"
        leftSection={<IconSearch size={20} aria-hidden />}
      />
      <Spotlight.ActionsList>
        {entryGroups.map(({ group, entries: groupedEntries }) => (
          <Spotlight.ActionsGroup key={group} role="group" label={group} aria-label={group}>
            {groupedEntries.map(({ id, label, description, Icon, path }) => (
              <Spotlight.Action
                key={id}
                label={label}
                description={description}
                onClick={() => navigate(path)}
                data-testid={`omni-search-result-${id}`}
                leftSection={<Icon size={20} aria-hidden />}
              />
            ))}
          </Spotlight.ActionsGroup>
        ))}
        {entryGroups.length === 0 && (
          <Spotlight.Empty data-testid="omni-search-empty">
            No pages match &quot;{query}&quot;
          </Spotlight.Empty>
        )}
      </Spotlight.ActionsList>
    </Spotlight.Root>
  );
}

/**
 * Splits ranked entries under their group headings. The group holding the best match comes first,
 * so the first result on screen, which Enter picks, is always the best match.
 */
function groupEntries(entries: OmniSearchEntry[]): EntryGroup[] {
  return entries.reduce<EntryGroup[]>((groups, entry) => {
    const existingGroup = groups.find(({ group }) => group === entry.group);
    if (existingGroup) {
      existingGroup.entries.push(entry);
    } else {
      groups.push({ group: entry.group, entries: [entry] });
    }
    return groups;
  }, []);
}
